import { z } from "zod";

/**
 * Format de fil entre l'API et ses clients. Ce paquet ne dépend ni du domaine
 * ni de l'API : c'est le contrat, et rien d'autre. Les états des bonbons y sont
 * donc redéclarés volontairement — un test côté API vérifie qu'ils ne dérivent
 * pas de ceux du domaine.
 */
export const treatStatusSchema = z.enum(["available", "exhausted", "none"]);

export type TreatStatusDto = z.infer<typeof treatStatusSchema>;

/**
 * Ce qu'une maison propose : deux axes indépendants, parce qu'une maison
 * décorée peut aussi distribuer des bonbons.
 */
export interface OfferDto {
  readonly treats: TreatStatusDto;
  readonly decorated: boolean;
}

/** Une pastille doit annoncer quelque chose, sinon elle n'apprend rien. */
const announcesSomething = (offer: OfferDto): boolean => offer.treats !== "none" || offer.decorated;

const NOTHING_ANNOUNCED = "Précisez des bonbons, ou une maison décorée.";

export const spotSchema = z.object({
  id: z.string().uuid(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  treats: treatStatusSchema,
  decorated: z.boolean(),
  note: z.string().max(140).nullable(),
  declaredAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  reportCount: z.number().int().min(0),
  /** Ce visiteur est l'auteur : il peut retirer sa propre pastille. */
  mine: z.boolean(),
});

export type SpotDto = z.infer<typeof spotSchema>;

/** Fenêtre de carte, passée en query string (`?south=…&west=…`). */
export const listSpotsQuerySchema = z.object({
  south: z.coerce.number().min(-90).max(90),
  west: z.coerce.number().min(-180).max(180),
  north: z.coerce.number().min(-90).max(90),
  east: z.coerce.number().min(-180).max(180),
});

export type ListSpotsQuery = z.infer<typeof listSpotsQuerySchema>;

export const listSpotsResponseSchema = z.object({
  spots: z.array(spotSchema),
  /** Le plafond de points a été atteint : il en existe d'autres dans la fenêtre. */
  truncated: z.boolean(),
  /** La fenêtre est trop large pour être interrogée : la carte invite à zoomer. */
  zoomedOut: z.boolean(),
});

export type ListSpotsResponse = z.infer<typeof listSpotsResponseSchema>;

export const declareSpotBodySchema = z
  .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    treats: treatStatusSchema,
    decorated: z.boolean(),
    note: z.string().max(140).nullish(),
  })
  .refine(announcesSomething, { message: NOTHING_ANNOUNCED, path: ["treats"] });

export type DeclareSpotBody = z.infer<typeof declareSpotBodySchema>;

export const reportSpotResponseSchema = z.object({
  hidden: z.boolean(),
  /** Contributeurs distincts ayant signalé — un contributeur ne compte qu'une fois. */
  reportCount: z.number().int().min(0),
  /** `true` si ce visiteur avait déjà signalé : rien n'a été compté. */
  alreadyReported: z.boolean(),
});

export type ReportSpotResponse = z.infer<typeof reportSpotResponseSchema>;

export const takeDownSpotResponseSchema = z.object({
  /** Toujours `true` : le retrait demandé par l'occupant est immédiat. */
  hidden: z.boolean(),
});

export type TakeDownSpotResponse = z.infer<typeof takeDownSpotResponseSchema>;
